import dayjs from 'dayjs';
import { Divider, HelpText, HorizontalList } from 'src/components/atoms';
import { DefaultOption, FormInput } from 'src/components/molecules';
import { TimeframeInput } from 'src/graphql';
import { FormInputVariant, FormState } from 'src/types';
import {
  DateFormats,
  formatDate,
  getYear,
  monthNames,
  setDate,
  setMonth,
  setYear,
} from 'src/utils/time';
import {
  CustomTimeframeFilterStyled,
  DividerContainer,
  TimeTitle,
} from './CustomTimeframeFilter.styles';

interface Props {
  timeframe: TimeframeInput;
  onChange: (timeframe: TimeframeInput) => void;
  oldestSelectableYear: number;
  state?: FormState;
  hasBottomDivider?: boolean;
  paddingBottom?: number;
}
enum TimeFrameEnum {
  Start = 'start',
  End = 'end',
}

enum TimeFrameTypes {
  Dates = 'dates',
  Months = 'months',
  Years = 'years',
}

export const CustomTimeframeFilter = ({
  timeframe,
  onChange,
  oldestSelectableYear,
  state = FormState.DEFAULT,
  hasBottomDivider = false,
  paddingBottom,
}: Props) => {
  const getFormattedTimeframe = () => ({
    start: formatDate(timeframe.start, DateFormats.YYYY_MM_DD),
    end: formatDate(timeframe.end, DateFormats.YYYY_MM_DD),
  });

  const today = dayjs();
  const currentYear = today.year();
  const currentMonth = today.month();
  const currentDate = today.date();

  const timeframeYears = Array(getYear() - oldestSelectableYear + 1)
    .fill(oldestSelectableYear)
    .map((year, i) => ({ label: year + i, value: year + i }));

  const timeframeMonths = monthNames.map((m, index) => ({
    label: m,
    value: index.toString(),
  }));

  const getTimeframeOptions = (time: TimeFrameEnum, type: TimeFrameTypes) => {
    if (type === TimeFrameTypes.Years) {
      if (time === TimeFrameEnum.End) {
        return timeframeYears.filter((year) => year.value <= currentYear);
      }
      return timeframeYears;
    }

    if (type === TimeFrameTypes.Months) {
      if (time === TimeFrameEnum.End) {
        const endYear = getYear(timeframe.end, true);
        if (endYear === currentYear) {
          return timeframeMonths.filter((_, index) => index <= currentMonth);
        }
      }
      return timeframeMonths;
    }

    if (type === TimeFrameTypes.Dates) {
      const selectedDate = dayjs(getFormattedTimeframe()[time]);
      const daysInMonth = selectedDate.daysInMonth();
      let maxDay = daysInMonth;

      if (
        time === TimeFrameEnum.End &&
        selectedDate.year() === currentYear &&
        selectedDate.month() === currentMonth
      ) {
        maxDay = currentDate;
      }

      return Array(maxDay)
        .fill(1)
        .map((_, i) => ({
          label: (i + 1).toString(),
          value: (i + 1).toString(),
        }));
    }

    return [];
  };

  return (
    <CustomTimeframeFilterStyled>
      <TimeTitle>Custom timeframe</TimeTitle>
      <HorizontalList padding={10} paddingBottom={paddingBottom}>
        <FormInput
          variant={FormInputVariant.DROPDOWN}
          options={getTimeframeOptions(
            TimeFrameEnum.Start,
            TimeFrameTypes.Dates,
          )}
          onChange={(o) =>
            onChange({
              ...timeframe,
              start: setDate(
                Number((o as DefaultOption)?.value),
                timeframe.start,
                'start',
                true,
              ).toDate(),
            })
          }
          defaultValue={{
            value: formatDate(timeframe.start, DateFormats.DAY),
            label: formatDate(timeframe.start, DateFormats.DAY),
          }}
          state={state}
          value={{
            value: formatDate(timeframe.start, DateFormats.DAY),
            label: formatDate(timeframe.start, DateFormats.DAY),
          }}
          data-testid="start-day-dropdown"
          $containerWidth="70px"
          width="70px"
        />
        <FormInput
          variant={FormInputVariant.DROPDOWN}
          options={getTimeframeOptions(
            TimeFrameEnum.Start,
            TimeFrameTypes.Months,
          )}
          onChange={(o) =>
            onChange({
              ...timeframe,
              start: setMonth(
                (o as DefaultOption)?.label as string,
                timeframe.start,
                'start',
                true,
              ).toDate(),
            })
          }
          defaultValue={{
            value: formatDate(timeframe.start, DateFormats.MONTH_LONG),
            label: formatDate(timeframe.start, DateFormats.MONTH_LONG),
          }}
          state={state}
          value={{
            value: formatDate(timeframe.start, DateFormats.MONTH_LONG),
            label: formatDate(timeframe.start, DateFormats.MONTH_LONG),
          }}
          data-testid="start-month-dropdown"
        />
        <FormInput
          variant={FormInputVariant.DROPDOWN}
          options={getTimeframeOptions(
            TimeFrameEnum.Start,
            TimeFrameTypes.Years,
          )}
          onChange={(o) =>
            onChange({
              ...timeframe,
              start: setYear(
                Number((o as DefaultOption)?.value),
                timeframe.start,
                true,
              )
                .startOf('month')
                .toDate(),
            })
          }
          defaultValue={{
            value: getYear(timeframe.start, true),
            label: getYear(timeframe.start, true),
          }}
          state={state}
          value={{
            value: getYear(timeframe.start, true),
            label: getYear(timeframe.start, true),
          }}
          data-testid="start-year-dropdown"
          $containerWidth="90px"
          width="90px"
        />
        _
        <FormInput
          variant={FormInputVariant.DROPDOWN}
          options={getTimeframeOptions(TimeFrameEnum.End, TimeFrameTypes.Dates)}
          onChange={(o) =>
            onChange({
              ...timeframe,
              end: setDate(
                Number((o as DefaultOption)?.value),
                timeframe.end,
                'end',
                true,
              ).toDate(),
            })
          }
          defaultValue={{
            value: formatDate(timeframe.end, DateFormats.DAY),
            label: formatDate(timeframe.end, DateFormats.DAY),
          }}
          state={state}
          value={{
            value: formatDate(timeframe.end, DateFormats.DAY),
            label: formatDate(timeframe.end, DateFormats.DAY),
          }}
          data-testid="end-day-dropdown"
          $containerWidth="70px"
          width="70px"
        />
        <FormInput
          variant={FormInputVariant.DROPDOWN}
          options={getTimeframeOptions(
            TimeFrameEnum.End,
            TimeFrameTypes.Months,
          )}
          onChange={(o) =>
            onChange({
              ...timeframe,
              end: setMonth(
                (o as DefaultOption)?.label as string,
                timeframe.end,
                'end',
                true,
              ).toDate(),
            })
          }
          defaultValue={{
            value: formatDate(timeframe.end, DateFormats.MONTH_LONG),
            label: formatDate(timeframe.end, DateFormats.MONTH_LONG),
          }}
          state={state}
          value={{
            value: formatDate(timeframe.end, DateFormats.MONTH_LONG),
            label: formatDate(timeframe.end, DateFormats.MONTH_LONG),
          }}
          data-testid="end-month-dropdown"
        />
        <FormInput
          variant={FormInputVariant.DROPDOWN}
          options={getTimeframeOptions(TimeFrameEnum.End, TimeFrameTypes.Years)}
          onChange={(o) =>
            onChange({
              ...timeframe,
              end: setYear(
                Number((o as DefaultOption)?.value),
                timeframe.end,
                true,
              )
                .endOf('month')
                .toDate(),
            })
          }
          defaultValue={{
            value: getYear(timeframe.end, true),
            label: getYear(timeframe.end, true),
          }}
          state={state}
          value={{
            value: getYear(timeframe.end, true),
            label: getYear(timeframe.end, true),
          }}
          data-testid="end-year-dropdown"
          $containerWidth="90px"
          width="90px"
        />
      </HorizontalList>
      {state === FormState.ERROR && (
        <HelpText
          data-testid="custom-time-error-message"
          state={state}
          helpText="The timeframe should be in chronological order"
        />
      )}
      {hasBottomDivider && (
        <DividerContainer hasErrorMessage={state === FormState.ERROR}>
          <Divider variant="horizontal" />
        </DividerContainer>
      )}
    </CustomTimeframeFilterStyled>
  );
};
